import React from 'react'
import PropTypes from 'prop-types'
import { Typography, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import styles from './TableEmptyState.module.scss'

/**
 * TableEmptyState - A component to display when table has no data
 *
 * @param {Object} props - Component props
 * @param {boolean} props.searched - Whether a search has been performed
 * @param {string} props.title - The title to display
 * @param {string} props.message - The message to display
 * @param {string} props.icon - The icon to display
 * @param {Object} props.dateRange - Date range for displaying in the message
 * @param {Function} props.renderCustomContent - Callback to render custom content
 */
const TableEmptyState = ({
  searched = false,
  title = '',
  message = '',
  icon = 'search',
  dateRange = null,
  renderCustomContent = null
}) => {
  const { t } = useTranslation()

  const defaultTitle = searched
    ? t('common.noResultsFound')
    : t('common.noDataAvailable')

  const defaultMessage = searched
    ? t('common.noMatchingRecords')
    : t('common.searchToSeeResults')

  const iconToUse = searched ? 'database' : icon

  return (
    <Box className={styles.container}>
      <Box className={styles.iconContainer}>
        <FontAwesomeIcon
          icon={iconToUse}
          className={styles.icon}
        />
      </Box>

      <Typography variant='h5' component='p' color='textSecondary' gutterBottom className={styles.title}>
        {title || defaultTitle}
      </Typography>

      <Typography
        variant='body1'
        color='textSecondary'
        align='center'
        className={styles.message}
      >
        {message || defaultMessage}
        {dateRange && (
          <span>
            {' '}
            <strong>
              {new Date(dateRange.start).toLocaleDateString()} - {new Date(dateRange.end).toLocaleDateString()}
            </strong>
          </span>
        )}
      </Typography>

      {renderCustomContent && (
        <Box className={styles.customContent}>
          {renderCustomContent()}
        </Box>
      )}
    </Box>
  )
}

TableEmptyState.propTypes = {
  searched: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  icon: PropTypes.string,
  dateRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  renderCustomContent: PropTypes.func
}

export default TableEmptyState
